import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSuitcase } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import TableList from '../../components/Tools/TableList';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';

/**
 * « Mes ordres de mission » de l'espace collaborateur (Yros/Kavero#128).
 *
 * Patron de `MesBulletins.js` : la liste passe par le tunnel (`perimetre=moi`,
 * contrat Yros/Kavero#125). Yros/Kavero#439 (T11) a retiré le téléchargement
 * du PDF : l'original se retire au bureau, le salarié ne suit que le statut
 * (Yros/Kavero#423). L'écran ne retrie rien : Odoo rend déjà du départ le plus
 * récent au plus ancien. Pas de `HeaderCard`, la coquille le pose déjà.
 */

// Les quatre valeurs de Yros/Kavero#423. Une valeur hors liste (backend plus
// récent, ou pas encore migré) s'affiche en tiret plutôt que par sa clé brute.
const STATUTS = ['en_attente', 'disponible', 'remis', 'annule'];
function MesOrdresMission() {
  const { t } = useTranslation();
  const [ordres, setOrdres] = useState([]);
  const [chargement, setChargement] = useState(true);
  // Drapeaux, pas messages : le libellé se calcule au rendu et suit la langue.
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let actif = true;
    (async () => {
      try {
        const reponse = await apiService.listerOrdresMission('moi');
        if (!actif) return;
        if (reponse?.ok === false) {
          setErreur(true);
        } else {
          setOrdres(reponse?.ordres || []);
        }
      } catch (e) {
        if (!actif) return;
        signalerErreur(e, 'MesOrdresMission.charger');
        setErreur(true);
      } finally {
        if (actif) setChargement(false);
      }
    })();
    return () => {
      actif = false;
    };
  }, []);

  const colonnes = [
    {
      key: 'numero',
      label: t('mesOrdresMission.colNumero'),
      render: (row) => (
        <span data-testid={`mes-ordres-numero-${row.id}`} className="font-medium text-slate-800">
          {row.numero}
        </span>
      ),
    },
    {
      key: 'destination',
      label: t('mesOrdresMission.colDestination'),
      render: (row) => row.destination,
    },
    {
      key: 'periode',
      label: t('mesOrdresMission.colPeriode'),
      render: (row) => `${row.date_depart} → ${row.date_retour}`,
    },
    {
      key: 'statut',
      label: t('mesOrdresMission.colStatut'),
      render: (row) => (
        <span data-testid={`mes-ordres-statut-${row.id}`} className="text-sm text-slate-700">
          {STATUTS.includes(row.statut) ? t(`mesOrdresMission.statut.${row.statut}`) : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100">
      <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-100">
        <FaSuitcase className="w-4 h-4 shrink-0 text-[--color-primary]" aria-hidden="true" />
        <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
          {t('mesOrdresMission.titre')}
        </h3>
      </div>

      {erreur && (
        <p
          data-testid="mes-ordres-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {t('mesOrdresMission.erreurListe')}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : erreur ? null : ordres.length === 0 ? (
        // Pas de `TableList` à vide : son état vide parle de critères de
        // recherche, ce qui ne dit rien au salarié (même choix que MesBulletins).
        <p data-testid="mes-ordres-vide" className="py-8 text-center text-sm text-gray-500">
          {t('mesOrdresMission.vide')}
        </p>
      ) : (
        <TableList
          columns={colonnes}
          data={ordres}
          searchDisplay={false}
        />
      )}
    </div>
  );
}

export default MesOrdresMission;
